import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, Tag, Clock, Car } from 'lucide-react';
import type { StandardItem } from '../../types/standard';
import { WalkIcon, BookIcon, BikeIcon } from '../icons';
import { BaseDetailCard } from '../shared/BaseDetailCard';
import { useImageDisplay } from '../../hooks/useImageDisplay';
import { useDetailCardTranslation } from '../../hooks/useDetailCardTranslation';
import { useFavoriteToggle } from '../../hooks/useFavoriteToggle';
import { isEmptyField } from '../../utils/timeUtils';
import { formatPhoneNumber } from '../../utils/formatters';
import { formatTravelTime } from '../../utils/formatDuration';
import { getHotelItineraryUrl } from '../../utils/itineraryUtils';
import { getTranslatedText, getTranslatedCategory } from '../../utils/translationHelpers';
import { useSection } from '../../hooks/useSection';
import { useAnalytics } from '../../hooks/useAnalytics';

interface DetailCardProps {
  item: StandardItem;
  items: StandardItem[];
  onClose: () => void;
  onItemChange: (item: StandardItem) => void;
  onCategorySelect: (category: string) => void;
  onTypeSelect: (type: string) => void;
  config: any;
  translations: Record<string, Record<string, string>>;
  tableId: string;
  categoryColors: Map<string, string>;
  behavior?: 'default' | 'pin' | 'place';
  showIndex?: number | null;
  place?: any;
  onShowPlace?: (place: any) => void;
}

export const DetailCard: React.FC<DetailCardProps> = ({
  item,
  items,
  onClose,
  onItemChange,
  onCategorySelect,
  onTypeSelect,
  config,
  translations,
  tableId,
  categoryColors,
  behavior,
  showIndex,
  place,
  onShowPlace
}) => {
  const { i18n } = useTranslation();
  const section = useSection();
  const { displayImage, shouldDisplay } = useImageDisplay(item.image, 'detailCard');

  const analytics = useAnalytics({
    sectionId: section.id,
    sectionName: section.names.fr,
    sectionType: 'standard'
  });

  const { getDescription, getOffer, translatingDescription, descriptionError } = useDetailCardTranslation({
    itemId: item.id,
    itemName: item.name,
    description: item.description,
    offer: item.offer,
    descriptions: item.descriptions,
    offers: item.offers,
    tableId,
    sectionType: 'standard'
  });

  const { isFavorite, handleFavoriteToggle } = useFavoriteToggle({
    itemId: item.id,
    itemName: item.name,
    category: item.category,
    type: item.type,
    sectionType: 'standard',
    itemData: item
  });

  useEffect(() => {
    const startTime = Date.now();
    return () => {
      const duration = (Date.now() - startTime) / 1000;
      analytics.trackEvent('content_view', 'detail_card_view', {
        item_id: item.id,
        item_name: item.name,
        item_type: 'standard',
        category: item.category,
        type: item.type,
        duration,
        source: 'detail_card'
      });
    };
  }, [item.id, analytics]);

  const translate = (key: string) => getTranslatedText(translations, key, i18n.language);
  const translateCat = (key: string) => getTranslatedCategory(translations, key, i18n.language);

  const renderContent = (item: StandardItem) => (
    <div className="flex flex-col gap-1 text-sm text-gray-600 mb-2">
      <div className="w-full">
        {item.fulladress && <span>{item.fulladress}</span>}
        {item.fulladress && item.telephone && <span> &bull; </span>}
        {item.telephone && <span>{formatPhoneNumber(item.telephone)}</span>}
      </div>

      <div className="flex flex-wrap gap-2">
        {!isEmptyField(item.distance) && (
          <div className="flex items-center">
            <MapPin size={15} className="mr-1" />
            <span>{item.distance}km</span>
          </div>
        )}
        {!item.inHotel && (
          <>
            {!isEmptyField(item.timewalk) && (
              <div className="flex items-center">
                <WalkIcon size={15} className="mr-1" />
                <span>{formatTravelTime(item.timewalk, i18n.language)}</span>
              </div>
            )}
            {!isEmptyField(item.timebike) && (
              <div className="flex items-center">
                <BikeIcon size={15} className="mr-1" />
                <span>{formatTravelTime(item.timebike, i18n.language)}</span>
              </div>
            )}
            {!isEmptyField(item.timedrive) && (
              <div className="flex items-center">
                <Car size={15} className="mr-1" fill="currentColor" />
                <span>{formatTravelTime(item.timedrive, i18n.language)}</span>
              </div>
            )}
          </>
        )}
        {item.price && (
          <div className="flex items-center" style={{ color: config['major-color'] }}>
            <Tag size={15} className="mr-1" />
            <span>{item.price}</span>
          </div>
        )}
        {item.ouverture_lundi === 'oui' && (
          <div className="flex items-center">
            <Clock size={15} className="mr-1" />
            <span>{translate('events_monday_open')}</span>
          </div>
        )}
      </div>
    </div>
  );

  const renderBookingButton = (item: StandardItem) => {
    if (!item.booking) return null;

    return (
      <a
        href={item.booking}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-center px-4 py-2 rounded-lg text-white mb-2"
        style={{ backgroundColor: config['major-color'] }}
        onClick={() => {
          analytics.trackItemAction(item.id, 'booking_click', 'standard', {
            item_name: item.name,
            url: item.booking
          });
        }}
      >
        <BookIcon size={18} className="mr-2" />
        {translate('btn_booking')}
      </a>
    );
  };

  return (
    <BaseDetailCard
      item={item}
      items={items}
      onClose={onClose}
      onItemChange={onItemChange}
      config={config}
      translations={translations}
      tableId={tableId}
      renderContent={renderContent}
      renderBookingButton={renderBookingButton}
      onCategorySelect={onCategorySelect}
      onTypeSelect={onTypeSelect}
      categoryColors={categoryColors}
      getTranslatedCategory={translateCat}
      getTranslatedText={translate}
      itineraryUrl={getHotelItineraryUrl(item.fulladress, config.fulladress)}
      description={getDescription()}
      offer={getOffer()}
      opening_hours={item.opening_hours}
      translating={translatingDescription}
      translationError={descriptionError}
      context={{
        sectionType: 'standard',
        itemId: item.id,
        section_id: section.id,
        contentType: 'description'
      }}
      behavior={behavior}
      showIndex={showIndex}
      place={place}
      onShowPlace={onShowPlace}
      isFavorite={isFavorite}
      onFavoriteToggle={handleFavoriteToggle}
      image={shouldDisplay ? displayImage : undefined}
    />
  );
};

export default DetailCard;
